import type { ScheduleActivityStep } from '../actionSectionTypes';
import { ASSIGNMENT_TYPES, SCHEDULE_TYPES } from './constants';
import type { ScheduleActivityDetails } from './types';

export const isDeletedAssignedToVariable = (
  variableName: string,
  assignedTo: ScheduleActivityDetails['assignedTo']
) =>
  (assignedTo.assignmentType ===
    ASSIGNMENT_TYPES.SA_TYPE_TEAM_MEMBER_FROM_VARIABLE ||
    assignedTo.assignmentType ===
      ASSIGNMENT_TYPES.SA_TYPE_ROLE_FROM_VARIABLE) &&
  assignedTo.variable?.value === variableName;

export const isDeletedOnAndAroundDateTimeOffsetVariable = (
  variableName: string,
  onOrAroundDate: ScheduleActivityDetails['schedule']['onOrAroundDate']
) =>
  onOrAroundDate.timeOffsetType === 'automation_variable' &&
  onOrAroundDate?.timeOffsetVariable?.value === variableName;

export const isDeletedOnAndAroundDateVariable = (
  variableName: string,
  onOrAroundDate: ScheduleActivityDetails['schedule']['onOrAroundDate']
) => onOrAroundDate?.variable?.value === variableName;

export const isDeletedOnAndAroundDateDateOffsetVariable = (
  variableName: string,
  onOrAroundDate: ScheduleActivityDetails['schedule']['onOrAroundDate']
) =>
  onOrAroundDate?.dateOffsetType === 'automation_variable' &&
  onOrAroundDate?.dateOffsetVariable?.value === variableName;

export const isDeletedWithDelayDelayVariable = (
  variableName: string,
  withDelay: ScheduleActivityDetails['schedule']['withDelay']
) =>
  withDelay.delayValueType === 'automation_variable' &&
  withDelay?.delayVariable?.value === variableName;

export const isDeletedWithDelayTimeOffsetVariable = (
  variableName: string,
  withDelay: ScheduleActivityDetails['schedule']['withDelay']
) =>
  withDelay.timeOffsetType === 'automation_variable' &&
  withDelay?.timeOffsetVariable?.value === variableName;

export const isDeletedAssociationVariable = (
  variableName: string,
  details: ScheduleActivityDetails
) =>
  details?.associationConfigs?.some(
    (associationConfig) =>
      associationConfig.automationVariable?.value === variableName
  );

export const isDeletedTargetVariable = (
  variableName: string,
  details: ScheduleActivityDetails
) => details?.targetVariable?.value === variableName;

export const scheduleActivityRemoveVariable = (
  step: ScheduleActivityStep,
  variableName: string,
  errorIds: string[]
) => {
  const nextErrorIds: string[] = [];

  let { hasError } = step;
  let { assignedTo, schedule, associationConfigs, targetVariable } =
    step.details;

  if (assignedTo && isDeletedAssignedToVariable(variableName, assignedTo)) {
    if (!nextErrorIds.includes(step.id)) {
      nextErrorIds.push(step.id);
    }
    hasError = true;
    assignedTo = {
      ...assignedTo,
      variable: { ...assignedTo.variable!, deleted: true },
    };
  }

  if (schedule && schedule?.type === SCHEDULE_TYPES.WITH_DELAY) {
    let { withDelay } = schedule;

    if (isDeletedWithDelayDelayVariable(variableName, withDelay)) {
      if (!nextErrorIds.includes(step.id)) {
        nextErrorIds.push(step.id);
      }
      hasError = true;
      withDelay = {
        ...withDelay,
        delayVariable: { ...withDelay.delayVariable!, deleted: true },
      };
    }
    if (isDeletedWithDelayTimeOffsetVariable(variableName, withDelay)) {
      if (!nextErrorIds.includes(step.id)) {
        nextErrorIds.push(step.id);
      }
      hasError = true;
      withDelay = {
        ...withDelay,
        timeOffsetVariable: { ...withDelay.timeOffsetVariable!, deleted: true },
      };
    }
    schedule = {
      ...schedule,
      withDelay,
    };
  }

  if (
    schedule &&
    schedule?.type === SCHEDULE_TYPES.ON_OR_AROUND_DATE_VARIABLE
  ) {
    let { onOrAroundDate } = schedule;

    if (
      isDeletedOnAndAroundDateVariable(variableName, onOrAroundDate) ||
      onOrAroundDate?.variable?.value === variableName
    ) {
      if (!nextErrorIds.includes(step.id)) {
        nextErrorIds.push(step.id);
      }
      hasError = true;
      onOrAroundDate = {
        ...onOrAroundDate,
        variable: { ...onOrAroundDate.variable!, deleted: true },
      };
    }

    if (
      isDeletedOnAndAroundDateTimeOffsetVariable(variableName, onOrAroundDate)
    ) {
      if (!nextErrorIds.includes(step.id)) {
        nextErrorIds.push(step.id);
      }
      hasError = true;
      onOrAroundDate = {
        ...onOrAroundDate,
        timeOffsetVariable: {
          ...onOrAroundDate.timeOffsetVariable!,
          deleted: true,
        },
      };
    }

    if (
      isDeletedOnAndAroundDateDateOffsetVariable(variableName, onOrAroundDate)
    ) {
      if (!nextErrorIds.includes(step.id)) {
        nextErrorIds.push(step.id);
      }
      hasError = true;
      onOrAroundDate = {
        ...onOrAroundDate,
        dateOffsetVariable: {
          ...onOrAroundDate.dateOffsetVariable!,
          deleted: true,
        },
      };
    }

    schedule = {
      ...schedule,
      onOrAroundDate,
    };
  }

  if (isDeletedAssociationVariable(variableName, step.details)) {
    if (!nextErrorIds.includes(step.id)) {
      nextErrorIds.push(step.id);
    }
    hasError = true;
    associationConfigs = associationConfigs?.map((associationConfig) => {
      if (associationConfig.automationVariable?.value === variableName) {
        return {
          ...associationConfig,
          automationVariable: {
            ...associationConfig.automationVariable!,
            deleted: true,
          },
        };
      }
      return associationConfig;
    });
  }

  if (isDeletedTargetVariable(variableName, step.details)) {
    if (!nextErrorIds.includes(step.id)) {
      nextErrorIds.push(step.id);
    }
    hasError = true;
    targetVariable = { ...targetVariable!, deleted: true };
  }

  return {
    nextStep: {
      ...step,
      details: {
        ...step.details,
        assignedTo,
        schedule,
        associationConfigs,
        targetVariable,
      },
      hasError,
    },
    nextErrorIds,
  };
};
